import Wallet from '@/utils/wallet'
import type { AssetConfig } from './types'

/**
 * Raw asset registry. Each row specifies the canonical identifiers —
 * name, ticker, CoinGecko id (slug + cached mcap/ATH), Hyperliquid perp
 * symbol (live price + chart), and MoonPay currency code. Everything else
 * (iconUrl, description fallback, twitterSearchQuery) is filled in by
 * `expand()` at module load.
 *
 * Adding a new asset = append a row here. The dynamic route
 * /markets/[asset] picks it up, the asset switcher dropdown lists it,
 * and the global search surfaces it — all without further edits.
 *
 * Coverage rule: every asset here MUST be listed on Hyperliquid, since
 * the price + chart come from Hyperliquid. Hyperliquid quotes a handful
 * of low-priced memecoins in thousands under a "k" symbol (kPEPE, kSHIB,
 * kBONK, kFLOKI) — those carry hyperliquidPxScale: 0.001 so the displayed
 * per-token price is correct. Assets Hyperliquid doesn't list (stablecoins
 * like USDT/USDC, and some alts) are intentionally omitted.
 */
type RegistryEntry = {
	symbol: string
	name: string
	coingeckoId: string
	hyperliquidSymbol: string
	moonpayCurrencyCode: string
	hyperliquidPxScale?: number
	description?: string
	twitterSearchQuery?: string
	iconUrl?: string
	getUserAddress?: () => string | null
}

const raw: RegistryEntry[] = [
	{
		symbol: 'BTC',
		name: 'Bitcoin',
		coingeckoId: 'bitcoin',
		hyperliquidSymbol: 'BTC',
		moonpayCurrencyCode: 'btc',
		description:
			"Bitcoin is the world's first decentralized digital currency — a peer-to-peer network secured by proof-of-work, with no banks, no intermediaries, and a fixed supply of 21 million coins.",
		getUserAddress: () => (Wallet.isSet() ? Wallet.balanceAddress() : null)
	},
	{
		symbol: 'ETH',
		name: 'Ethereum',
		coingeckoId: 'ethereum',
		hyperliquidSymbol: 'ETH',
		moonpayCurrencyCode: 'eth',
		description:
			'Ethereum is the largest smart-contract platform — a programmable blockchain that powers DeFi, stablecoins, NFTs, and most of the EVM-compatible ecosystem.'
	},
	{
		symbol: 'SOL',
		name: 'Solana',
		coingeckoId: 'solana',
		hyperliquidSymbol: 'SOL',
		moonpayCurrencyCode: 'sol',
		description:
			'Solana is a high-throughput proof-of-stake blockchain optimized for fast, low-fee transactions — popular for memecoins, DePIN, mobile-friendly DeFi, and consumer apps.'
	},
	{
		symbol: 'BNB',
		name: 'BNB',
		coingeckoId: 'binancecoin',
		hyperliquidSymbol: 'BNB',
		moonpayCurrencyCode: 'bnb_bsc'
	},
	{
		symbol: 'XRP',
		name: 'XRP',
		coingeckoId: 'ripple',
		hyperliquidSymbol: 'XRP',
		moonpayCurrencyCode: 'xrp'
	},
	{
		symbol: 'ADA',
		name: 'Cardano',
		coingeckoId: 'cardano',
		hyperliquidSymbol: 'ADA',
		moonpayCurrencyCode: 'ada'
	},
	{
		symbol: 'DOGE',
		name: 'Dogecoin',
		coingeckoId: 'dogecoin',
		hyperliquidSymbol: 'DOGE',
		moonpayCurrencyCode: 'doge'
	},
	{
		symbol: 'TRX',
		name: 'TRON',
		coingeckoId: 'tron',
		hyperliquidSymbol: 'TRX',
		moonpayCurrencyCode: 'trx'
	},
	{
		symbol: 'AVAX',
		name: 'Avalanche',
		coingeckoId: 'avalanche-2',
		hyperliquidSymbol: 'AVAX',
		moonpayCurrencyCode: 'avax_cchain'
	},
	{
		symbol: 'DOT',
		name: 'Polkadot',
		coingeckoId: 'polkadot',
		hyperliquidSymbol: 'DOT',
		moonpayCurrencyCode: 'dot'
	},
	{
		symbol: 'LTC',
		name: 'Litecoin',
		coingeckoId: 'litecoin',
		hyperliquidSymbol: 'LTC',
		moonpayCurrencyCode: 'ltc'
	},
	{
		symbol: 'BCH',
		name: 'Bitcoin Cash',
		coingeckoId: 'bitcoin-cash',
		hyperliquidSymbol: 'BCH',
		moonpayCurrencyCode: 'bch'
	},
	{
		symbol: 'LINK',
		name: 'Chainlink',
		coingeckoId: 'chainlink',
		hyperliquidSymbol: 'LINK',
		moonpayCurrencyCode: 'link'
	},
	{
		symbol: 'TON',
		name: 'Toncoin',
		coingeckoId: 'the-open-network',
		hyperliquidSymbol: 'TON',
		moonpayCurrencyCode: 'ton'
	},
	{
		symbol: 'NEAR',
		name: 'NEAR Protocol',
		coingeckoId: 'near',
		hyperliquidSymbol: 'NEAR',
		moonpayCurrencyCode: 'near'
	},
	{
		symbol: 'ATOM',
		name: 'Cosmos',
		coingeckoId: 'cosmos',
		hyperliquidSymbol: 'ATOM',
		moonpayCurrencyCode: 'atom'
	},
	{
		symbol: 'APT',
		name: 'Aptos',
		coingeckoId: 'aptos',
		hyperliquidSymbol: 'APT',
		moonpayCurrencyCode: 'apt'
	},
	{
		symbol: 'ARB',
		name: 'Arbitrum',
		coingeckoId: 'arbitrum',
		hyperliquidSymbol: 'ARB',
		moonpayCurrencyCode: 'arb_arbitrum'
	},
	{
		symbol: 'OP',
		name: 'Optimism',
		coingeckoId: 'optimism',
		hyperliquidSymbol: 'OP',
		moonpayCurrencyCode: 'op_optimism'
	},
	{
		symbol: 'SUI',
		name: 'Sui',
		coingeckoId: 'sui',
		hyperliquidSymbol: 'SUI',
		moonpayCurrencyCode: 'sui'
	},
	{
		symbol: 'SEI',
		name: 'Sei',
		coingeckoId: 'sei-network',
		hyperliquidSymbol: 'SEI',
		moonpayCurrencyCode: 'sei'
	},
	{
		symbol: 'INJ',
		name: 'Injective',
		coingeckoId: 'injective-protocol',
		hyperliquidSymbol: 'INJ',
		moonpayCurrencyCode: 'inj'
	},
	{
		symbol: 'FIL',
		name: 'Filecoin',
		coingeckoId: 'filecoin',
		hyperliquidSymbol: 'FIL',
		moonpayCurrencyCode: 'fil'
	},
	{
		symbol: 'UNI',
		name: 'Uniswap',
		coingeckoId: 'uniswap',
		hyperliquidSymbol: 'UNI',
		moonpayCurrencyCode: 'uni'
	},
	{
		symbol: 'AAVE',
		name: 'Aave',
		coingeckoId: 'aave',
		hyperliquidSymbol: 'AAVE',
		moonpayCurrencyCode: 'aave'
	},
	{
		symbol: 'PEPE',
		name: 'Pepe',
		coingeckoId: 'pepe',
		hyperliquidSymbol: 'kPEPE',
		hyperliquidPxScale: 0.001,
		moonpayCurrencyCode: 'pepe'
	},
	{
		symbol: 'SHIB',
		name: 'Shiba Inu',
		coingeckoId: 'shiba-inu',
		hyperliquidSymbol: 'kSHIB',
		hyperliquidPxScale: 0.001,
		moonpayCurrencyCode: 'shib'
	},
	{
		symbol: 'BONK',
		name: 'Bonk',
		coingeckoId: 'bonk',
		hyperliquidSymbol: 'kBONK',
		hyperliquidPxScale: 0.001,
		moonpayCurrencyCode: 'bonk'
	},
	{
		symbol: 'FLOKI',
		name: 'Floki',
		coingeckoId: 'floki',
		hyperliquidSymbol: 'kFLOKI',
		hyperliquidPxScale: 0.001,
		moonpayCurrencyCode: 'floki'
	}
]

function defaultDescription(name: string, symbol: string): string {
	return `${name} (${symbol}) is a cryptocurrency available to buy and sell instantly via MoonPay on Ordinals Wallet.`
}

function expand(entry: RegistryEntry): AssetConfig {
	// MoonPay's icon CDN uses the base ticker (e.g. `btc.svg`, `eth.svg`,
	// `trx.svg`), not the full currency code with network suffix
	// (`bnb_bsc.svg` doesn't exist; `bnb.svg` does). Default to the
	// lowercased ticker so most icons resolve out of the box.
	const defaultIcon = `https://static.moonpay.com/widget/currencies/${entry.symbol.toLowerCase()}.svg`
	return {
		symbol: entry.symbol,
		name: entry.name,
		description: entry.description ?? defaultDescription(entry.name, entry.symbol),
		coingeckoId: entry.coingeckoId,
		hyperliquidSymbol: entry.hyperliquidSymbol,
		hyperliquidPxScale: entry.hyperliquidPxScale,
		iconUrl: entry.iconUrl ?? defaultIcon,
		moonpayCurrencyCode: entry.moonpayCurrencyCode,
		twitterSearchQuery: entry.twitterSearchQuery ?? entry.name.toLowerCase(),
		getUserAddress: entry.getUserAddress
	}
}

export const ASSETS: AssetConfig[] = raw.map(expand)
